import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  jobService,
  Job,
  CreateJobDto,
  UpdateJobDto,
  JobSearchDto,
  ChangeJobStatusDto,
  CreateJobFromAppointmentDto,
  DuplicateJobDto,
} from '../services/jobService';
import { queryKeys } from '../config/queryKeys';

interface UseJobsParams extends JobSearchDto {
  enabled?: boolean;
}

export function useJobs(params: UseJobsParams = {}) {
  const { enabled = true, ...searchDto } = params;

  return useQuery({
    queryKey: queryKeys.jobs.list(searchDto),
    queryFn: () => jobService.getJobs(searchDto),
    enabled,
    placeholderData: (prev) => prev, // Keep previous while loading
  });
}

export function useJob(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.jobs.detail(id!),
    queryFn: () => jobService.getJob(id!),
    enabled: !!id,
  });
}

export function useJobsByCustomer(customerId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.jobs.byCustomer(customerId!),
    queryFn: () => jobService.getJobsByCustomer(customerId!),
    enabled: !!customerId,
  });
}

// Mutations
export function useCreateJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateJobDto) => jobService.createJob(data),
    onSuccess: (newJob) => {
      // Invalidate job lists to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
      // Invalidate customer's jobs
      queryClient.invalidateQueries({
        queryKey: queryKeys.jobs.byCustomer(newJob.customerId),
      });
      // Invalidate customer detail (job count may have changed)
      queryClient.invalidateQueries({
        queryKey: queryKeys.customers.detail(newJob.customerId),
      });
    },
  });
}

export function useUpdateJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateJobDto) => jobService.updateJob(data),
    onSuccess: (updatedJob) => {
      // Update the specific job in cache
      queryClient.setQueryData(
        queryKeys.jobs.detail(updatedJob.id),
        updatedJob
      );
      // Invalidate lists to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
      // Invalidate customer's jobs
      queryClient.invalidateQueries({
        queryKey: queryKeys.jobs.byCustomer(updatedJob.customerId),
      });
    },
  });
}

export function useChangeJobStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChangeJobStatusDto) => jobService.changeJobStatus(data),
    onSuccess: (_, data) => {
      // Invalidate the specific job
      queryClient.invalidateQueries({
        queryKey: queryKeys.jobs.detail(data.jobId),
      });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
    },
  });
}

export function useDeleteJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => jobService.deleteJob(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.jobs.detail(id) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
      // Invalidate related data
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.all });
    },
  });
}

export function useCreateJobFromAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateJobFromAppointmentDto) =>
      jobService.createJobFromAppointment(data),
    onSuccess: (newJob) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.jobs.byCustomer(newJob.customerId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customers.detail(newJob.customerId),
      });
      // Invalidate the appointment as it may now be linked
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });
    },
  });
}

export function useDuplicateJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DuplicateJobDto) => jobService.duplicateJob(data),
    onSuccess: (newJob) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.jobs.byCustomer(newJob.customerId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customers.detail(newJob.customerId),
      });
    },
  });
}
